/**
 * A-GTCC-VIEW-BOUNDARY1 — containment for the GTCC content slot.
 *
 * A-GTCC-BOUNDARY-MESS1 measured that apps/admin had no error boundary at all:
 * a render throw in one view tore down the whole React root, and the Owner was
 * left with a blank page — no cockpit frame, no navigation, no text, only a
 * reload helped. This boundary keeps such a throw inside `<main>`; shell,
 * topbar and navigation stay usable.
 *
 * Semantics mirror `apps/web/src/components/ErrorBoundary.tsx` (#2299):
 * `getDerivedStateFromError` for the fallback, `componentDidCatch` for the
 * console, `reset()` for the retry, and the request-id of an `ApiError` so an
 * operator can correlate with the structured access log.
 *
 * Scope: mounted once, around the active view (`GtccShell.tsx`), with
 * `key={activeView.key}` so switching views resets it. No reporting to the API
 * (an Owner decision, and a privacy question); no root boundary — a throw in
 * the shell itself still tears the tree down, as `gtcc-view-boundary.test.tsx`
 * pins.
 */
import { Button, Card, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQueryClient } from "@tanstack/react-query";
import { Component, type ErrorInfo, type ReactNode, useCallback } from "react";

import { ApiError } from "../lib/api.js";

interface GtccViewBoundaryProps {
  readonly children: ReactNode;
  /**
   * Runs before the boundary resets its state. The wrapper below uses it to
   * drop the view's cache entries; the class itself stays free of hooks.
   */
  readonly onBeforeReset?: () => void;
}

interface GtccViewBoundaryState {
  readonly error: Error | null;
}

function GtccViewErrorCard({
  error,
  onRetry,
}: {
  readonly error: Error;
  readonly onRetry: () => void;
}) {
  const { t } = useLocale();
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  return (
    <Card elevation={1}>
      <Stack gap={3} role="alert" data-testid="gtcc-view-boundary-fallback">
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
          {t("admin.gtcc.view_error.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
          {t("admin.gtcc.view_error.body")}
        </p>
        {requestId ? (
          <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
            {t("admin.gtcc.view_error.request_id", { requestId })}
          </p>
        ) : null}
        <div>
          <Button variant="secondary" onClick={onRetry} data-testid="gtcc-view-boundary-retry">
            {t("admin.gtcc.view_error.retry")}
          </Button>
        </div>
      </Stack>
    </Card>
  );
}

export class GtccViewErrorBoundary extends Component<GtccViewBoundaryProps, GtccViewBoundaryState> {
  override state: GtccViewBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): GtccViewBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Direct console.error like the web boundary: the tree is already in an
    // error state and a structured logger may not be reachable. Stack and
    // component trace land in the browser console, where an operator can
    // capture them. There is no client-side error channel in apps/admin.
    console.error("GtccViewBoundary caught", { error, info });
  }

  reset = (): void => {
    this.props.onBeforeReset?.();
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return <GtccViewErrorCard error={error} onRetry={this.reset} />;
  }
}

/**
 * A-GTCC-VIEW-BOUNDARY2 — the retry drops the active view's cache entries.
 *
 * A-GTCC-VIEW-BOUNDARY1 measured that resetting the boundary's state alone did
 * not heal the payload-id-only cases: the view re-rendered, read the same
 * broken payload out of the react-query cache and threw again. The client is
 * only reachable through a hook, so this small function component sits around
 * the class and hands it an `onBeforeReset`.
 *
 * The keys come from `queryPrefixes` in the registry, measured per view — a
 * blanket `["gtcc"]` would also drop the global Ask Genius panel, and the
 * second key segment is not always the view key (`aia` fetches under
 * `insights-analytics`, `mail`'s data hangs under `["admin", …]`).
 * `removeQueries`, not `resetQueries`: the entry should be gone so the view
 * mounts as it would on a first visit.
 */
export function GtccViewBoundary({
  children,
  queryPrefixes = [],
}: {
  readonly children: ReactNode;
  readonly queryPrefixes?: readonly (readonly unknown[])[];
}): ReactNode {
  const queryClient = useQueryClient();
  const onBeforeReset = useCallback(() => {
    for (const prefix of queryPrefixes) {
      queryClient.removeQueries({ queryKey: [...prefix] });
    }
  }, [queryClient, queryPrefixes]);
  return <GtccViewErrorBoundary onBeforeReset={onBeforeReset}>{children}</GtccViewErrorBoundary>;
}
